import React, { useState, useEffect, useRef } from 'react';
import './Editor.css';
import { useNavigate, useLocation } from 'react-router-dom';
import EditorSection from './components/EditorSection';
import ModelOutputSection from './components/ModelOutputSection';
import ConfigSection from './components/ConfigSection';
import axios from 'axios';
import Tutorial from './components/Tutorial';
import InfoTooltip from './components/InfoTooltip';

/**
 * Main App component for poetry interface with 4 scenarios and logging system:
 * - human-only: editor + Publish
 * - curator   : editor + AI suggestions + full config + generate
 * - co-write  : editor + auto-complete panel
 * - ai-only   : AI suggestions + full config + generate
 */

const logConfigChange = async (pid, scenario, configField, oldValue, newValue, configState) => {
    try {
        await fetch('/api/log/config', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pid,
                scenario,
                config_field: configField,
                old_value: oldValue,
                new_value: newValue,
                config_state: configState
            })
        });
    } catch (error) {
        console.error('Error logging config change:', error);
    }
};

const logModelSelection = async (pid, scenario, selectedResponses, allResponses) => {
    try {
        await fetch('/api/log/model-selection', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pid,
                scenario,
                selected_responses: selectedResponses,
                all_responses: allResponses
            })
        });
    } catch (error) {
        console.error('Error logging model selection:', error);
    }
};

// these logs of pause and resume help us to purify the poem creation time
const logSessionEvent = async (pid, scenario, type, meta = {}) => {
    try {
        await fetch('/api/log/editor', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pid,
                scenario,
                action_type: type,          // "pause" | "resume"
                editor_content: null,
                content_diff: null,
                additional_data: meta
            })
        });
    } catch (e) {
        console.error('Error logging session event:', e);
    }
};

function PoemEditor() {
    const navigate = useNavigate();
    const { state, search } = useLocation();
    const params = new URLSearchParams(search);
    const [showTutorial, setShowTutorial] = useState(false);

    const withSearch = (extra = {}) => {
        const sp = new URLSearchParams(search || '');
        Object.entries(extra).forEach(([k, v]) => {
            if (v === undefined || v === null) sp.delete(k);
            else sp.set(k, String(v));
        });
        return `?${sp.toString()}`;
    };

    const scenarioLocked =
        new URLSearchParams(search || '').get('lock') === '1' ||
        localStorage.getItem('scenarioLocked') === 'true';


    const pid = params.get('pid') || localStorage.getItem('pid') || 'unknown';

    // 1) Grab passed-back scenario from state, or from the query param, else default
    const initialScenario =
        state?.scenario ||
        params.get('scenario') ||
        localStorage.getItem('scenario') ||
        'human-only';

    // 2) Grab draft text if any
    const initialContent = state?.poemDraft || '';

    const [editorContent, setEditorContent] =
        useState(initialContent);
    const [scenario, setScenario] =
        useState(initialScenario);
    const [modelOutput, setModelOutput] = useState([]);
    const [config, setConfig] = useState({
        creativity: 'high',
        poeticElement: 'none',
        rhymeSchema: 'none',
        numResponses: 1,
        inspiration: '',
        lineLimit: 4,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessionStartTime] = useState(Date.now());

    //helpers for purifying the process time
    const [isPaused, setIsPaused] = useState(false);
    const pauseStartRef = useRef(null);
    const [pausedTotalMs, setPausedTotalMs] = useState(0);

    useEffect(() => { localStorage.setItem('scenario', scenario); }, [scenario]);
    useEffect(() => { if (pid && pid !== 'unknown') localStorage.setItem('pid', pid); }, [pid]);


    // restore from sessionStorage if you used "Tutorial → Back"
    useEffect(() => {
        const urlScenario = params.get('scenario');
        const saved = sessionStorage.getItem('poemEditorState');
        if (saved && !urlScenario) {
            const {
                editorContent,
                config,
                modelOutput,
                scenario: restoredScenario,
            } = JSON.parse(saved);
            setEditorContent(editorContent);
            setConfig(config);
            setModelOutput(modelOutput);
            setScenario(restoredScenario);
            sessionStorage.removeItem('poemEditorState');
        }
    }, [search]);

    const handleScenarioChange = (next) => {
        if (scenarioLocked) return;
        if (
            !window.confirm(
                'Switching scenarios will clear your current draft. Continue?'
            )
        )
            return;

        const oldScenario = scenario;
        setScenario(next);
        setEditorContent('');
        setModelOutput([]);

        logConfigChange(pid, oldScenario, 'scenario', oldScenario, next, { scenario: next });

        if (next === 'curator' || next === 'ai-only') {
            setConfig({
                creativity: 'high',
                poeticElement: 'none',
                rhymeSchema: 'none',
                numResponses: 1,
                inspiration: '',
                lineLimit: 4,
            });
        } else if (next === 'co-write') {
            setConfig({ creativity: 'high', lineLimit: 1 });
        } else if (next === 'human-only') {
            setConfig({});
        }
        navigate(
            { pathname: '/write-poem', search: withSearch({ interface: 'write', scenario: next, pid }) },
            { replace: true }
        );
    };

    // Curator & AI-Only → /generate
    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = {
                creativity: config.creativity,
                poetic_element: config.poeticElement,
                rhyme_schema: config.rhymeSchema,
                num_responses: config.numResponses,
                inspiration: config.inspiration || null,
                line_limit: config.lineLimit,
                current_draft:
                    scenario === 'curator' ? editorContent : '',
                pid: pid,
                scenario: scenario
            };
            // const response = await axios.post(
            //     'http://localhost:5001/api/generate',
            //     payload,
            //     { timeout: 60000 }
            // );
            // const response = await axios.post(
            //   'http://140.238.156.35:5001/api/generate',
            //   payload,
            //   { timeout: 60000 }
            // );
            const response = await axios.post(
              '/api/generate',
              payload,
              { timeout: 60000 }
            );
            if (response.data.success) {
                setModelOutput(response.data.poetry);
            } else {
                setError('Failed to generate poetry');
            }
        } catch (err) {
            console.error(err);
            setError('Error connecting to server. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Curator →
    const handleAddToDraft = (selectedOutputs) => {
        if (!selectedOutputs.length) return;

        logModelSelection(pid, scenario, selectedOutputs, modelOutput);
        setEditorContent((prev) =>
            prev + '\n\n' + selectedOutputs.join('\n\n')
        );
    };

    // Co-write → /autocomplete
    const handleAutoComplete = async () => {
        if (!editorContent.trim()) return;
        setLoading(true);
        setError(null);
        try {
            // const resp = await axios.post(
            //     'http://localhost:5001/api/autocomplete',
            //     {
            //         draft: editorContent,
            //         num_lines: config.lineLimit,
            //         creativity: config.creativity,
            //         pid: pid,
            //         scenario: scenario
            //     },
            //     { timeout: 60000 }
            // );
            // const resp = await axios.post(
            //   'http://140.238.156.35:5001/api/autocomplete',
            //   {
            //     draft: editorContent,
            //     num_lines: config.lineLimit,
            //     creativity: config.creativity,
            //     pid: pid,
            //     scenario: scenario
            //   },
            //   { timeout: 60000 }
            // );
            const resp = await axios.post(
              '/api/autocomplete',
              {
                draft: editorContent,
                num_lines: config.lineLimit,
                creativity: config.creativity,
                pid: pid,
                scenario: scenario
              },
              { timeout: 60000 }
            );
            if (resp.data.success) {
                setEditorContent((prev) =>
                    prev + '\n' + resp.data.lines.join('\n')
                );
            } else {
                setError('Auto-complete failed');
            }
        } catch (err) {
            console.error(err);
            setError('Server error during auto-complete');
        } finally {
            setLoading(false);
        }
    };

    const handleConfigChange = (newConfig) => {
        const oldConfig = { ...config };
        const updatedConfig = { ...config, ...newConfig };

        Object.keys(newConfig).forEach(key => {
            if (oldConfig[key] !== newConfig[key]) {
                logConfigChange(pid, scenario, key, oldConfig[key], newConfig[key], updatedConfig);
            }
        });

        setConfig(updatedConfig);
    };

    const handleAIOnlyPublish = (choice) => {
        logModelSelection(pid, scenario, [choice], modelOutput);

        // const creationDuration = Math.floor((Date.now() - sessionStartTime) / 1000);
        const creationDuration = Math.floor((Date.now() - sessionStartTime - pausedTotalMs) / 1000);

        navigate(
            { pathname: '/tag-poem', search: withSearch({ interface: 'write', type: 'editor', scenario, pid }) },
            { state: { poemDraft: choice, scenario, pid, creationDuration } }
        );
    };
    //handler functions for purifying the process time
    const handlePause = () => {
        if (isPaused) return;
        pauseStartRef.current = Date.now();
        setIsPaused(true);
        logSessionEvent(pid, scenario, 'pause', {
            paused_total_ms: pausedTotalMs
        });
    };

    const handleResume = () => {
        if (!isPaused) return;
        const delta = Date.now() - (pauseStartRef.current || Date.now());
        const newTotal = pausedTotalMs + delta;
        setPausedTotalMs(newTotal);
        setIsPaused(false);
        logSessionEvent(pid, scenario, 'resume', {
            paused_delta_ms: delta,
            paused_total_ms: newTotal
        });
    };

    // pause the time for when the tab is hidden
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === 'hidden' && !isPaused) handlePause();
            if (document.visibilityState === 'visible' && isPaused) handleResume();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [isPaused]);

    return (
        <div className="App">
            <button
                onClick={() => navigate('/')}
                className="back-button"
            >
                ← Back to Main Page
            </button>

            <div className="header-controls">
                {/*commented for disabling scenario change*/}
                {/*<div className="scenario-switch">*/}
                {/*    <button*/}
                {/*        className={scenario === 'human-only' ? 'active' : ''}*/}
                {/*        onClick={() =>*/}
                {/*            handleScenarioChange('human-only')*/}
                {/*        }*/}
                {/*    >*/}
                {/*        Human-only*/}
                {/*    </button>*/}
                {/*    <button*/}
                {/*        className={scenario === 'curator' ? 'active' : ''}*/}
                {/*        onClick={() =>*/}
                {/*            handleScenarioChange('curator')*/}
                {/*        }*/}
                {/*    >*/}
                {/*        Curator*/}
                {/*    </button>*/}
                {/*    <button*/}
                {/*        className={scenario === 'co-write' ? 'active' : ''}*/}
                {/*        onClick={() =>*/}
                {/*            handleScenarioChange('co-write')*/}
                {/*        }*/}
                {/*    >*/}
                {/*        Co-write*/}
                {/*    </button>*/}
                {/*    <button*/}
                {/*        className={scenario === 'ai-only' ? 'active' : ''}*/}
                {/*        onClick={() =>*/}
                {/*            handleScenarioChange('ai-only')*/}
                {/*        }*/}
                {/*    >*/}
                {/*        AI Only*/}
                {/*    </button>*/}
                {/*</div>*/}
                <button
                    className="tutorial-button"
                    onClick={() => {
                        sessionStorage.setItem(
                            'poemEditorState',
                            JSON.stringify({
                                editorContent,
                                config,
                                modelOutput,
                                scenario,
                            })
                        );
                        // navigate({
                        //     pathname: '/tutorial',
                        //     search: withSearch({ interface: 'write', type: 'editor', scenario })
                        // });
                        setShowTutorial(true);
                    }}
                >
                    Tutorial
                </button>

                {showTutorial && (
                    <Tutorial
                        inline
                        onClose={() => setShowTutorial(false)}
                        mode="write"
                        type="editor"
                        scenario={scenario}   // current scenario
                        initialX={window.innerWidth - 700}
                        initialY={120}
                    />
                )}


                {!isPaused && (
                    <button
                        className="tutorial-button"
                        onClick={handlePause}
                        title="Pause session"
                    >
                        ⏸ Pause
                    </button>
                )}
            </div>

            <div className="app-content">
                {/* Human-only */}
                {scenario === 'human-only' && (
                    <div className="left-panel">
                        <EditorSection
                            content={editorContent}
                            onContentChange={setEditorContent}
                            scenario={scenario}
                            pid={pid}
                            sessionStartTime={sessionStartTime}
                            pausedTotalMs={pausedTotalMs}
                        />
                    </div>
                )}

                {/* Curator */}
                {scenario === 'curator' && (
                    <>
                        <div className="left-panel">
                            <EditorSection
                                content={editorContent}
                                onContentChange={setEditorContent}
                                scenario={scenario}
                                pid={pid}
                                sessionStartTime={sessionStartTime}
                                pausedTotalMs={pausedTotalMs}
                            />
                            <ModelOutputSection
                                output={modelOutput}
                                loading={loading}
                                error={error}
                                onAddToDraft={handleAddToDraft}
                                scenario={scenario}
                                pid={pid}
                            />
                        </div>
                        <div className="right-panel">
                            <ConfigSection
                                config={config}
                                onConfigChange={handleConfigChange}
                                onGenerate={handleGenerate}
                                loading={loading}
                                scenario={scenario}
                                editorContent={editorContent}
                                pid={pid}
                            />
                        </div>
                    </>
                )}

                {/* Co-write */}
                {scenario === 'co-write' && (
                    <>
                        <div className="left-panel">
                            <EditorSection
                                content={editorContent}
                                onContentChange={setEditorContent}
                                scenario={scenario}
                                pid={pid}
                                sessionStartTime={sessionStartTime}
                                pausedTotalMs={pausedTotalMs}
                            />
                        </div>
                        <div className="right-panel short-panel">
                            <ConfigSection
                                config={config}
                                onConfigChange={handleConfigChange}
                                onGenerate={handleAutoComplete}
                                loading={loading}
                                scenario={scenario}
                                editorContent={editorContent}
                                pid={pid}
                            />
                        </div>
                    </>
                )}

                {/* AI-only */}
                {scenario === 'ai-only' && (
                    <>
                        <div className="left-panel">
                            <ModelOutputSection
                                output={modelOutput}
                                loading={loading}
                                error={error}
                                onPublish={handleAIOnlyPublish}
                                scenario={scenario}
                                pid={pid}
                                sessionStartTime={sessionStartTime}
                            />
                        </div>
                        <div className="right-panel">
                            <ConfigSection
                                config={config}
                                onConfigChange={handleConfigChange}
                                onGenerate={handleGenerate}
                                loading={loading}
                                scenario={scenario}
                                pid={pid}
                            />
                        </div>
                    </>
                )}
            </div>
            {isPaused && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        backdropFilter: 'blur(2px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <button
                        onClick={handleResume}
                        style={{
                            fontSize: 18,
                            padding: '12px 24px',
                            borderRadius: 999,
                            border: 'none',
                            cursor: 'pointer',
                            background: '#fff'
                        }}
                        title="Resume session"
                    >
                        ▶ Resume
                    </button>
                </div>
            )}
        </div>
    );
}

export default PoemEditor;
